import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import BonlivList from '../components/bonliv/BonlivList';
import BonlivUpdateForm from '../components/bonliv/BonlivupdateForm';
import BonlivDeleteButton from '../components/bonliv/BonlivDeleteButton';
import SingleBonliv from '../components/bonliv/SingleBonliv';
import ItemsInBonlivList from '../components/bonliv/ItemInBonlivList';
import UpdateItemInBonlivForm from '../components/bonliv/updateItemInBonlivForm';
import DeleteItemFromBonlivButton from '../components/bonliv/DeleteItemFromBonlivButton';
import UnsavedChangesGuard from '../utils/UnsavedChangesGuard';
import { useSelector } from 'react-redux';
import { Modal } from 'antd';
import './styles/devis-animations.css';

const BonlivManagementPage = () => {
  const [currentView, setCurrentView] = useState('list');
  const [selectedBonlivId, setSelectedBonlivId] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const user = useSelector(state => state.user);
  const userPermissions = {
    isManager: user?.IsManager,
  };

  const handleChangesDetected = () => {
    setHasUnsavedChanges(true);
  };

  const handleChangesSaved = () => {
    setHasUnsavedChanges(false);
  };

  const handleViewChange = useCallback((newView, newItemId = null) => {
    if (hasUnsavedChanges) {
      Modal.confirm({
        title: 'Modif. non enregistrées',
        content: 'Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir quitter sans sauvegarder ?',
        okText: 'Quitter',
        cancelText: 'Rester',
        onOk: () => {
          setCurrentView(newView);
          if (newItemId !== null) {
            setSelectedItemId(newItemId);
          }
          setHasUnsavedChanges(false);
        },
      });
    } else {
      setCurrentView(newView);
      if (newItemId !== null) {
        setSelectedItemId(newItemId);
      }
    }
  }, [hasUnsavedChanges]);

  const handleViewItemInBonliv = (itemId) => {
    handleViewChange('viewItemInBonliv', itemId);
  };

  const handleSelectBonliv = (id) => {
    setSelectedBonlivId(id);
    handleViewChange('viewBonliv');
  };

  const handleConfirmNavigation = useCallback(() => {
    setHasUnsavedChanges(false);
  }, []);

  // Animation variants
  const pageVariants = {
    initial: {
      opacity: 0,
      scale: 0.97,
    },
    animate: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.15,
        ease: [0.25, 0.1, 0.25, 1.0],
      }
    },
    exit: {
      opacity: 0,
      scale: 0.97,
      transition: {
        duration: 0.12,
        ease: [0.25, 0.1, 0.25, 1.0],
      }
    }
  };

  const renderView = () => {
    switch (currentView) {
      case 'update':
        return <BonlivUpdateForm bonlivId={selectedBonlivId} />;
      case 'delete':
        return <BonlivDeleteButton bonlivId={selectedBonlivId} onSuccess={() => handleViewChange('list')} />;
      case 'list':
        return <BonlivList onSelectBonliv={handleSelectBonliv} userPermissions={userPermissions}/>;
      case 'viewBonliv':
        return selectedBonlivId && (
          <SingleBonliv 
            bonlivId={selectedBonlivId} 
            onSelectItem={handleViewItemInBonliv} 
            onChangeView={handleViewChange}
            userPermissions={userPermissions}
            onChangesSaved={handleChangesSaved}
            onChangesDetected={handleChangesDetected}
          />
        );
      case 'viewItems':
        return selectedBonlivId && (
          <ItemsInBonlivList refBonliv={selectedBonlivId} />
        );
      case 'viewItemInBonliv':
        return selectedBonlivId && selectedItemId && (
          <>
            <UpdateItemInBonlivForm refBonliv={selectedBonlivId} codeArt={selectedItemId} />
            <DeleteItemFromBonlivButton 
              refBonliv={selectedBonlivId} 
              codeArt={selectedItemId} 
              onSuccess={() => handleViewChange('viewBonliv')} 
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <UnsavedChangesGuard
      hasUnsavedChanges={hasUnsavedChanges}
      onConfirmNavigation={handleConfirmNavigation}
    >
      <div className="devis-management-container">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentView}
            initial="initial"
            animate="animate"
            exit="exit"
            variants={pageVariants}
            className="view-container"
            style={{
              width: '100%',
              height: '100%'
            }}
          >
            {renderView()}
          </motion.div>
        </AnimatePresence>
      </div>
    </UnsavedChangesGuard>
  );
};

export default BonlivManagementPage;